import React from 'react';
import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { Spin } from 'antd';
import {
    GET_STATION_LINK_TIMELINE,
    GetStationLinkTimelineResponse,
    FollowUpTimelineEventDto,
} from '../graphql/queries/getStationLinkTimeline';
import {
    AddressAbnormalContent,
    FirstContactContent,
    OnsiteContent,
    SecondContactContent,
    OnsiteProofs,
    OP_TYPE_I18N_KEY,
    PICKUP_FAILURE_CODE,
    parseContent,
} from '../utils/followUpTimeline';
import { formatHubTimeFull, useHubZone } from '../utils/hubTime';

interface Props {
    /** 站点 id（cooperation_warehouse.id） */
    cooperationWarehouseId?: number;
    /** 抽屉是否打开 —— 关闭时不发请求 */
    open: boolean;
}

/**
 * 站点跟进记录（只读，2.1 需求点7）。
 *
 * <p>挂在「查看揽收站点」详情底部，**仅 `link_status=5`（确认无法建联）站点展示**
 * （产品定案 2026-08-26）。是否渲染由调用方判断，本组件只负责取数与呈现。
 *
 * <p>与「司机反馈跟进」详情的区别：那边是阶段化卡片 + 内嵌操作（二次建联表单、更新站点按钮），
 * 这里是**纯只读的时间顺序列表** —— 站点详情本就是查看态，不该出现能改数据的控件。
 * 解析与照片渲染取自 {@link ../utils/followUpTimeline}；那边只有本组件在用，
 * 「司机反馈跟进」详情仍是各自一份 local 副本（同步要求见该文件头部说明）。
 *
 * <p><b>空态必须显示、不能整块隐藏</b>：生产实测 `link_status=5` 的 69 个站点里
 * **35 个（50.7%）没有任何跟进记录**（2.0 之前 / golive 批量刷状态的老站点没走过建联流程）。
 * 整块不渲染的话，运营看不到「跟进记录」区，分不清是"没这功能"还是"没记录"，会反复找。
 */
const StationFollowUpTimeline: React.FC<Props> = ({ cooperationWarehouseId, open }) => {
    // 时间统一按 hub 时区展示：服务端给的是 UTC 无后缀串（容器 TZ=UTC + serverTimezone=UTC），
    // 直接 new Date() 会当成浏览器本地，运营看到的时间与站点当地作业时间对不上。
    const hubZone = useHubZone();
    const { t } = useTranslation();
    const ft = (k: string) => t(`pickup.addressReviewPool.followUp.${k}`) as string;

    const { data, loading } = useQuery<GetStationLinkTimelineResponse>(GET_STATION_LINK_TIMELINE, {
        variables: { cooperationWarehouseId },
        skip: !open || cooperationWarehouseId == null,
        // network-only：跟进记录会被别处的操作追加（二次建联、司机反馈），缓存会让运营看到过期的进度
        fetchPolicy: 'network-only',
    });

    // 后端已按 id ASC 返回（= 发生先后）；此处不再排序，避免与后端口径漂移
    const events: FollowUpTimelineEventDto[] = data?.getStationLinkTimeline ?? [];

    const opLabel = (opType?: number | null): string => {
        if (opType == null) return ft('opUnknown');
        const key = OP_TYPE_I18N_KEY[opType];
        // 后端新增枚举而前端没跟上时显示原始码，不显示空白（空白无从排查）
        return key ? ft(key) : `${ft('opUnknown')}(${opType})`;
    };

    /** 按 op_type 把 content 渲染成人话；解析不出结构就当纯文本（RETIRE/MERGE 等写的就是纯文本） */
    const renderContent = (e: FollowUpTimelineEventDto): React.ReactNode => {
        const raw = e.content;
        const urls = e.proofS3Urls ?? undefined;

        if (e.operationType === 3) {
            const c = parseContent<FirstContactContent>(raw);
            if (!c) return raw || null;
            return (
                <>
                    <div>{c.result === 'contacted' ? ft('contacted') : ft('notContacted')}</div>
                    <OnsiteProofs keys={c.proofS3Keys} urls={urls} label={ft('proof')} />
                </>
            );
        }

        if (e.operationType === 5) {
            const c = parseContent<OnsiteContent>(raw);
            if (!c) return raw || null;
            // note 是纯失败原因码（ERPUxxx）时不展示 —— 运营看不懂码，且状态行已表达了失败
            const note = c.note && !PICKUP_FAILURE_CODE.test(c.note.trim()) ? c.note : undefined;
            return (
                <>
                    <div>
                        {c.state === 'success'
                            ? ft('onsiteSuccess')
                            : c.state === 'failed'
                              ? ft('onsiteFailed')
                              : ft('onsiteDispatched')}
                    </div>
                    {(c.driverName || c.driverPhone) && (
                        <div className="text-gray-500">
                            {ft('driver')} {c.driverName || '-'} {c.driverPhone ? `· ${c.driverPhone}` : ''}
                        </div>
                    )}
                    {c.onsiteTime && (
                        <div className="text-gray-500">
                            {ft('onsiteTime')} {formatHubTimeFull(c.onsiteTime, hubZone)}
                        </div>
                    )}
                    {(c.onsiteContactName || c.onsiteContactPhone) && (
                        <div className="text-gray-500">
                            {ft('onsiteContact')} {c.onsiteContactName || '-'}{' '}
                            {c.onsiteContactPhone ? `· ${c.onsiteContactPhone}` : ''}
                        </div>
                    )}
                    {note && <div className="text-gray-500">{note}</div>}
                    <OnsiteProofs keys={c.proofS3Keys} urls={urls} label={ft('proof')} />
                </>
            );
        }

        if (e.operationType === 6) {
            const c = parseContent<SecondContactContent>(raw);
            if (!c) return raw || null;
            return (
                <>
                    <div>{c.result === 'contacted' ? ft('contacted') : ft('notContacted')}</div>
                    {(c.groupName || c.groupId) && (
                        <div className="text-gray-500">
                            {ft('group')} {c.groupName || '-'} {c.groupId ? `· ${c.groupId}` : ''}
                        </div>
                    )}
                    <OnsiteProofs keys={c.proofS3Keys} urls={urls} label={ft('proof')} />
                </>
            );
        }

        // 地址异常上报（2.2 需求点1）：上报 / 编辑 / 撤销共用 type=12，靠 content.action 区分。
        // PRD 要求「异常上报结果同步到无法建联站点的建联记录中，**支持查看**」——
        // 只显示标题不算支持查看，运营要能在这里看到原因和现场凭证（凭证复用同一个 OnsiteProofs，可放大）。
        if (e.operationType === 12) {
            const c = parseContent<AddressAbnormalContent>(raw);
            // 解析不出就退回纯文本，与 3/5/6 同一兜底 —— 时间线不能因为一条脏 content 整块崩掉
            if (!c) return raw || null;
            const action = c.action === 'edit' || c.action === 'revoke' ? c.action : 'report';
            const actionKey = action === 'edit' ? 'timelineEdit' : action === 'revoke' ? 'timelineRevoke' : 'timelineReport';
            return (
                <>
                    <div>{t(`pickup.addressReviewPool.abnormal.${actionKey}`)}</div>
                    {/* 原因与备注都是自由输入且非必填，原样显示；两者可能都为空（此时只剩标题与凭证） */}
                    {c.reason && (
                        <div className="text-gray-500">
                            {t('pickup.addressReviewPool.abnormal.reasonInline')}
                            {c.reason}
                        </div>
                    )}
                    {c.remark && (
                        <div className="text-gray-500">
                            {t('pickup.addressReviewPool.abnormal.remarkInline')}
                            {c.remark}
                        </div>
                    )}
                    <OnsiteProofs
                        keys={c.proofS3Keys}
                        urls={urls}
                        label={t('pickup.addressReviewPool.abnormal.proofLabel')}
                    />
                </>
            );
        }

        // 其余 op_type（创建/更新/合并/AB映射/退役…）的 content 本就是纯文本
        return raw || null;
    };

    return (
        <div>
            <div className="mb-2 text-sm font-semibold text-gray-700">📋 {ft('title')}</div>
            {loading ? (
                <div className="py-4 text-center">
                    <Spin size="small" />
                </div>
            ) : events.length === 0 ? (
                // 空态：50.7% 的 link_status=5 站点会走到这里，必须说清"为什么没有"
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm text-gray-500">
                    {ft('empty')}
                </div>
            ) : (
                <div className="space-y-2">
                    {events.map((e) => (
                        <div key={e.id ?? `${e.operationType}-${e.time}`} className="rounded-lg border border-gray-200 p-3">
                            <div className="mb-1 flex items-center justify-between gap-2">
                                <span className="text-sm font-medium text-gray-800">{opLabel(e.operationType)}</span>
                                <span className="shrink-0 text-xs text-gray-400">{formatHubTimeFull(e.time, hubZone)}</span>
                            </div>
                            {e.operatorName && (
                                <div className="mb-1 text-xs text-gray-400">
                                    {ft('operator')} {e.operatorName}
                                </div>
                            )}
                            <div className="space-y-1 text-sm text-gray-700">{renderContent(e)}</div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default StationFollowUpTimeline;
